/**
 * Game type icons (fill-based, colored by trump/suit): the Grand chalice, Null
 * oval zero, Ramsch fallen chalice, and the suit-pip dispatcher that pictograph
 * each contract. See docs/components/ICONOGRAPHY.md § Game Type Icons.
 */
import { TRUMP_COLOR } from "../constants.js";
import { createGameTypePip, SUIT_COLORS } from "../cards/cards.js";
import { addArtisanPip } from "../cards/card-pips.js";
import { SVG_NS, svg, filled, detail } from "./svg.js";

/** Appends the chalice parts shared between upright and fallen variants — atmosphere ring,
 *  bowl body, facet line, stem, and knop. Callers append the base ellipse afterwards
 *  (and, for the upright variant only, a knop ring detail between knop and base). */
function appendChaliceBowlStemKnop(parent, color) {
  const atmoD =
    "M -14 -12 C -16 -28 16 -28 14 -12 C 14 0 6 6 0 8 C -6 6 -14 0 -14 -12 Z";
  const bodyD =
    "M -11 -9 C -13 -22 13 -22 11 -9 C 11 0 5 5 0 6 C -5 5 -11 0 -11 -9 Z";
  parent.appendChild(filled("path", { d: atmoD, opacity: 0.1 }, color));
  parent.appendChild(filled("path", { d: bodyD, opacity: 0.9 }, color));
  parent.appendChild(
    detail("path", {
      d: "M -10 -5 Q 0 -1 10 -5",
      "stroke-width": 1,
      opacity: 0.2,
    }),
  );
  parent.appendChild(
    filled("rect", { x: -1, y: 6, width: 2, height: 12, rx: 0.5 }, color),
  );
  parent.appendChild(filled("circle", { cx: 0, cy: 12, r: 3 }, color));
}

/** Grand game type icon: upright chalice with refined Artisan geometry. */
export function createChalice(size, color = TRUMP_COLOR) {
  const el = svg(size, size, "-20 -25 40 50");
  appendChaliceBowlStemKnop(el, color);
  // Knop ring detail (only on upright variant)
  el.appendChild(
    detail("circle", {
      cx: 0,
      cy: 12,
      r: 1,
      "stroke-width": 0.5,
      opacity: 0.3,
    }),
  );
  el.appendChild(filled("ellipse", { cx: 0, cy: 22, rx: 10, ry: 3 }, color));
  return el;
}

/** Null game type icon: oval zero (∅) with four suit-colored segments and cream slash. */
export function createNullZero(size) {
  const el = svg(size, size, "-16 -20 32 40");
  // Four filled segments (clockwise from top: amber/diamonds, red/hearts, green/spades, blue/clubs)
  const segments = [
    [
      "M 0 -16 A 12 16 0 0 1 12 0 L 7 0 A 7 10 0 0 0 0 -10 Z",
      SUIT_COLORS.diamonds,
    ],
    ["M 12 0 A 12 16 0 0 1 0 16 L 0 10 A 7 10 0 0 0 7 0 Z", SUIT_COLORS.hearts],
    [
      "M 0 16 A 12 16 0 0 1 -12 0 L -7 0 A 7 10 0 0 0 0 10 Z",
      SUIT_COLORS.spades,
    ],
    [
      "M -12 0 A 12 16 0 0 1 0 -16 L 0 -10 A 7 10 0 0 0 -7 0 Z",
      SUIT_COLORS.clubs,
    ],
  ];
  for (const [d, color] of segments) {
    el.appendChild(filled("path", { d }, color));
  }
  // Cream diagonal slash (∅)
  const slash = filled("line", { x1: -8, y1: 13, x2: 8, y2: -13 }, "none");
  slash.setAttribute("stroke", "#E2E8F0");
  slash.setAttribute("stroke-width", "3.5");
  slash.setAttribute("stroke-linecap", "round");
  el.appendChild(slash);
  return el;
}

/** Ramsch game type icon: chalice lying on its side (~80° rotation) with v7 geometry. */
export function createFallenChalice(size, color = TRUMP_COLOR) {
  const el = svg(size, size, "-25 -20 50 40");
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("transform", "rotate(80, 0, 0)");
  appendChaliceBowlStemKnop(g, color);
  g.appendChild(filled("ellipse", { cx: 0, cy: 22, rx: 10, ry: 3 }, color));
  el.appendChild(g);
  return el;
}

// The Farbspiel category mark (stats-v2 panel redesign design § grammar 3/6: the matrix's suit-row
// axis marker and every Farbspiel cell's typeToken) — all four suits at once, 2x2, reading order
// diamonds/hearts top, spades/clubs bottom (the catalog's own D-H-S-C suit-breakdown order). Flat
// intrinsic suit colors (SUIT_COLORS), not the trump gradient createGameTypePip draws — this marks
// "a Farbspiel round happened", not "this suit is trump" (games-list-enhance.js#chipTypeIcon is the
// same flat-vs-gradient call for the same reason).
const CLUSTER_PIPS = [
  ["diamonds", -9, -9],
  ["hearts", 9, -9],
  ["spades", -9, 9],
  ["clubs", 9, 9],
];
const CLUSTER_PIP_SCALE = 0.5;

export function createSuitCluster(size) {
  const el = svg(size, size, "-19 -19 38 38");
  for (const [suit, cx, cy] of CLUSTER_PIPS) {
    addArtisanPip(el, suit, cx, cy, CLUSTER_PIP_SCALE, SUIT_COLORS[suit]);
  }
  return el;
}

/**
 * Game type icon dispatcher: suit → pip, GRAND → chalice, NULL → zero, RAMSCH → fallen chalice.
 * Returns null for unknown types so callers can branch on truthiness.
 */
export function createGameTypeIcon(gameType, size) {
  if (["DIAMONDS", "HEARTS", "SPADES", "CLUBS"].includes(gameType)) {
    return createGameTypePip(gameType.toLowerCase(), size);
  }
  if (gameType === "GRAND") return createChalice(size);
  if (gameType === "NULL") return createNullZero(size);
  if (gameType === "RAMSCH") return createFallenChalice(size);
  return null;
}
